"use client";

import { ALGOLIA_HITS_PER_PAGE, ALGOLIA_PRODUCTS_INDEX } from "@raumagarn/algolia";
import type { AlgoliaProductCard } from "@raumagarn/formatters";
import { getLink, type MultilinkStoryblok, StoryblokComponent } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import type { Hit } from "instantsearch.js";
import type { InfiniteHitsCache } from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import { usePathname } from "next/navigation";
import { Suspense } from "react";
import { Configure, InstantSearch, useInfiniteHits, useSortBy } from "react-instantsearch";
import type { ISbStoryData } from "storyblok-js-client";
import { Breadcrumbs } from "~/components/ui/ProductBreadcrumbs";
import { sessionStorageCache } from "~/features/algolia/cache";
import { searchClient } from "~/features/algolia/client";
import {
	AlgoliaLoadMore,
	AlgoliaRefinementTags,
	AlgoliaSearchbar,
} from "~/features/algolia/components";
import { FilterButton } from "~/features/products/ProductListingPage/components/FilterButton";
import { FilterModal } from "~/features/products/ProductListingPage/components/FilterModal";
import { HeroParagraph } from "~/features/products/ProductListingPage/components/HeroParagraph/HeroParagraph";
import { Pagination } from "~/features/products/ProductListingPage/components/Pagination";
import { SortButton } from "~/features/products/ProductListingPage/components/SortButton";
import { ProductListingPageSkeleton } from "~/features/products/ProductListingPage/ProductListingPageSkeleton";
import { ProductSection } from "~/features/products/ProductSection";
import type { ProductListingPageStoryblok } from "~/generated/sb/blok-types";
import { Link } from "~/i18n/routing";
import type { Params } from "~/i18n/server";
import { GTMViewItemList } from "~/lib/gtm/components";
import { useCurrency } from "~/lib/hooks/useCentra";
import { useScrollToTop } from "~/lib/hooks/useScrollToTop";
import { getBaseUrl } from "~/lib/utils/getBaseUrl";
import { replaceLocalizedFilters } from "~/lib/utils/isLocalizedBooleanFilter";
import styles from "./ProductListingPage.module.css";

export interface ProductListingPageProps {
	story?: ISbStoryData<ProductListingPageStoryblok>;
	params: Params;
	preview: boolean;
}

const AlgoliaConfigure = ({ filters }: { filters?: string }) => {
	const { currentRefinement } = useSortBy({ items: [] });

	return (
		<Configure
			filters={filters}
			hitsPerPage={ALGOLIA_HITS_PER_PAGE}
			maxValuesPerFacet={100}
			clickAnalytics
			enableRules={currentRefinement === ALGOLIA_PRODUCTS_INDEX}
		/>
	);
};

const Tabs = ({
	tabs,
	slugs,
}: {
	tabs?: { text: string; link: MultilinkStoryblok }[];
	slugs?: string[];
}) => {
	return (
		<nav
			className={clsx(styles.heading__wrapper, (!tabs || tabs.length < 1) && styles["no-border"])}
		>
			{!tabs?.length && <h1>{slugs?.[slugs.length - 1]}</h1>}
			{tabs?.map((title, key) => {
				const url = getLink(title.link)?.href ?? "/";

				const [targetUrl, currentUrl] = (() => {
					const arr = url.split("/").filter((s) => {
						if (s === "") return false;
						if (s === "/") return false;
						if (!s) return false;
						return true;
					});
					return [arr[arr.length - 1], slugs?.[slugs.length - 1]];
				})();

				if (targetUrl === currentUrl) return <h1 key={key}>{title.text}</h1>;
				return (
					<Link href={url} key={key}>
						{title.text}
					</Link>
				);
			})}
		</nav>
	);
};

const UnsuspendedProductListingPage = ({ story, params, preview }: ProductListingPageProps) => {
	const scrollToTop = useScrollToTop();
	const {
		items: products,
		results,
		sendEvent,
	} = useInfiniteHits<AlgoliaProductCard>({
		cache: sessionStorageCache as unknown as InfiniteHitsCache<AlgoliaProductCard>,
	});
	const pathname = usePathname();
	return (
		<main>
			<Breadcrumbs
				crumbs={[
					{
						name: "Hjem",
						uri: "/",
					},

					...(params.slug?.map((s, i) => ({
						name: (() => {
							// sanitize category name
							const underscoreArr = s.split("_");
							const hyphenArr = underscoreArr?.[underscoreArr.length - 1]?.split("-");
							return hyphenArr?.join(" ");
						})(),
						uri: `/${params.slug?.slice(0, i + 1).join("/")}`,
					})) ?? []),
				]}
			/>
			{/* hero section */}
			<section className={styles.root} data-layout="page">
				<Tabs tabs={story?.content.tabs} slugs={params.slug} />

				{/* hero paragraph */}
				<HeroParagraph>
					{story?.content?.heroParagraph?.[0] && (
						<StoryblokComponent blok={story?.content?.heroParagraph?.[0]} preview={preview} />
					)}
				</HeroParagraph>
			</section>
			<GTMViewItemList
				products={products}
				categoryPage={pathname}
				categoryId={`${getBaseUrl()}${pathname}`}
			/>
			<section
				aria-label="filters & sorting"
				id="filters"
				data-layout="page"
				style={{ marginBottom: 16 }}
			>
				<AlgoliaSearchbar className={styles.mobile__search} />
				<div className={styles["filters-nav"]}>
					<FilterButton />
					{products?.length ? (
						<span className={styles["product-count"]}>{`${results?.nbHits ?? 0} produkter`}</span>
					) : null}
					<SortButton />
				</div>
				<AlgoliaRefinementTags />
			</section>
			<section
				aria-label="product section"
				data-layout="page"
				className={clsx(styles["main-content"])}
			>
				<FilterModal />
				<div className={styles.products__wrapper}>
					<ProductSection
						products={products ?? []}
						onProductClick={(product) =>
							sendEvent("click", product as unknown as Hit, "Product Clicked")
						}
					/>
					<AlgoliaLoadMore />
					{products?.length === results?.nbHits ? (
						<section data-layout="page">
							<Pagination
								type="end"
								data={{
									title: story?.content.endTitle || "",
									text: story?.content.endParagraph || "",
									buttonLabel: story?.content.endButtonLabel || "",
									onClick: scrollToTop,
								}}
							/>
						</section>
					) : null}
				</div>
			</section>
		</main>
	);
};

export const ProductListingPage: typeof UnsuspendedProductListingPage = (props) => {
	const { data: currency, isLoading } = useCurrency();

	if (isLoading) {
		return <ProductListingPageSkeleton />;
	}

	const formattedFilters = replaceLocalizedFilters(props.story?.content.algoliaRule, currency);

	return (
		<Suspense fallback={<ProductListingPageSkeleton />}>
			<InstantSearch
				indexName={ALGOLIA_PRODUCTS_INDEX}
				searchClient={searchClient}
				routing
				insights
			>
				<AlgoliaConfigure filters={formattedFilters} />
				<UnsuspendedProductListingPage {...props} />
			</InstantSearch>
		</Suspense>
	);
};
